
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import PinnedItemsModule from '@/store/modules/pinned-items';
import PinnedReportsModule from '@/store/modules/pinned-reports';

interface MenuPinnedItem {
  id: string;
  title: string;
  link: string;
  icon?: string;
  type: 'menu';
}

interface ReportPinnedItem {
  id: string;
  reportId: number;
  title: string;
  link: string;
  icon: string;
  type: 'report';
  timestamp: number;
}

type CombinedPinnedItem = MenuPinnedItem | ReportPinnedItem;

@Component
export default class PinnedSection extends Vue {
  @Prop({ required: true })
  public storeId!: number;
  
  @Prop({ default: false })
  public mini!: boolean;
  
  public expanded: boolean = true;
  private pinnedItemsStore = PinnedItemsModule;
  
  get pinnedItems(): CombinedPinnedItem[] {
    const menu: CombinedPinnedItem[] = this.pinnedItemsStore.getPinnedItems.map((m: any) => ({ ...m, type: 'menu' as const }));
    const reports: CombinedPinnedItem[] = PinnedReportsModule.getPinnedReports.map((r) => ({
      id: `report-${r.id}`,
      reportId: r.id,
      title: r.title,
      link: `/my-reports/${r.id}`,
      icon: 'mdi-file-chart-outline',
      type: 'report' as const,
      timestamp: Date.parse(r.pinnedAt) || 0,
    }));
    return [...menu, ...reports];
  }
  
  // Watch for changes to expanded state and save to localStorage
  @Watch('expanded')
  private onExpandedChanged(newVal: boolean): void {
    localStorage.setItem(`pinned_section_expanded_${this.storeId}`, String(newVal));
  }
  
  // Watch for changes to pinned items and expand when items are added
  @Watch('pinnedItems')
  private onPinnedItemsChanged(newItems: CombinedPinnedItem[], oldItems: CombinedPinnedItem[]): void {
    // If items were added (count increased), expand the section
    if (oldItems && newItems.length > oldItems.length) {
      this.expanded = true;
    }
  }
  
  private handleItemClick(item: CombinedPinnedItem): void {
    this.$emit('item-clicked', item);
  }

  private async handleUnpin(item: CombinedPinnedItem): Promise<void> {
    if (item.type === 'report') {
      await PinnedReportsModule.unpinReport(item.reportId);
    } else {
      await this.pinnedItemsStore.unpinItem({ itemId: item.id, storeId: this.storeId });
    }
    this.$emit('item-unpinned', item);
  }
  
  created(): void {
    // Load expanded state
    const savedState = localStorage.getItem(`pinned_section_expanded_${this.storeId}`);
    if (savedState !== null) {
      this.expanded = savedState === 'true';
    } else {
      // Default to expanded when first shown with items
      this.expanded = true;
      localStorage.setItem(`pinned_section_expanded_${this.storeId}`, 'true');
    }
  }
}
